import { useMemo, useState } from 'react';
import {
  MRT_EditActionButtons,
  MaterialReactTable,
  // createRow,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_SortingState,
  type MRT_Row,
  type MRT_TableOptions,
  useMaterialReactTable,
} from 'material-react-table';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Link,
  Snackbar,
  Alert,
  Chip,
  Divider,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from '@tanstack/react-query';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { usStates } from './makeData.ts';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import React from 'react';

import { type Course } from './Course.tsx'
import useCreateCourse from './CourseCreation.tsx'
import useGetCourses from './CourseGet.tsx'
import useUpdateCourse from './CourseUpdate.tsx'
import useDeleteCourse from './CourseDelete.tsx'
import BoxIndicateurCourse from './BoxIndicateurCourse.tsx'
import FicheCourse from './FicheCourse.tsx'
import AjoutCourseCalendrier from './AjoutCourseCalendrier.tsx'
import { estAdmin, estCoach, useMoi } from '../Auth/moi.ts';
import EventAvailableIcon from '@mui/icons-material/EventAvailableOutlined';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweepOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import { useEtatSession } from '../useEtatSession.ts';

// Sources des courses (colonne calendrier_course.source).
const SOURCES: Record<string, { libelle: string; description: string }> = {
  ISKIO: { libelle: 'iskio', description: 'Importée depuis iskio.ca (Québec / Canada)' },
  FFA: { libelle: 'FFA', description: 'Importée depuis le calendrier FFA (athle.fr)' },
  UTILISATEUR: { libelle: 'Perso', description: 'Ajoutée par toi' },
};

// Filtres rapides par distance (indicateurs "true"/"false" de la course).
type ChampDistance = 'indicateur_5k' | 'indicateur_10k' | 'indicateur_21k' | 'indicateur_42k';
const FILTRES_DISTANCE: { champ: ChampDistance; libelle: string }[] = [
  { champ: 'indicateur_5k', libelle: '5 km' },
  { champ: 'indicateur_10k', libelle: '10 km' },
  { champ: 'indicateur_21k', libelle: 'Semi-marathon' },
  { champ: 'indicateur_42k', libelle: 'Marathon' },
];

function estVrai(valeur: unknown): boolean {
  return String(valeur).toLowerCase() === 'true';
}

// Date du jour (fuseau local) au format AAAA-MM-JJ, comme dateest.
function aujourdhui(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Pays d'une course, deduit du code "province" : province canadienne ->
// Canada, etat americain -> Etats-Unis, sinon code pays (ex FR -> France).
const PROVINCES_CANADA = ['QC', 'ON', 'BC', 'AB', 'MB', 'SK', 'NS', 'NB', 'NL', 'PE', 'YT', 'NT', 'NU'];
const ETATS_USA = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC', 'PR',
];
let nomsPays: Intl.DisplayNames | null = null;
try {
  nomsPays = new Intl.DisplayNames(['fr'], { type: 'region' });
} catch {
  nomsPays = null;
}
function paysDe(course: { province?: string | null; pays?: string | null }): string {
  // Pays enregistre (deduit cote serveur, cf Geographie.java), sinon deduit ici.
  if (course.pays && course.pays.trim()) return course.pays.trim();
  const code = (course.province ?? '').trim().toUpperCase();
  if (!code) return 'Non renseigné';
  if (PROVINCES_CANADA.includes(code)) return 'Canada';
  if (ETATS_USA.includes(code)) return 'États-Unis';
  if (/^[A-Z]{2}$/.test(code)) {
    try {
      return nomsPays?.of(code) ?? code;
    } catch {
      return code;
    }
  }
  return code;
}

// Texte sans accents ni majuscules, pour la recherche par nom.
function normaliser(texte: string): string {
  return texte.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

// Course passee : date strictement anterieure a aujourd'hui. Une course sans
// date est consideree a venir (on ne la masque pas).
function estPassee(course: { dateest?: string | null }, dateDuJour: string): boolean {
  const date = course.dateest ? String(course.dateest).substring(0, 10) : '';
  return date !== '' && date < dateDuJour;
}

const Example = () => {
  const queryClient = useQueryClient();
  // Par defaut, seules les courses a venir (date >= aujourd'hui) sont
  // affichees ; les courses passees peuvent etre reaffichees, grisees.
  const [afficherPassees, setAfficherPassees] = useEtatSession('calendrier.filtre.passees', false);
  // Distances selectionnees : une course est affichee si elle propose au
  // moins l'une d'elles (aucune selection = toutes les courses).
  const [distances, setDistances] = useEtatSession<ChampDistance[]>('calendrier.filtre.distances', []);
  const [seulementBoston, setSeulementBoston] = useEtatSession('calendrier.filtre.boston', false);
  // Filtres : nom (contient), regions (choix multiple), pays.
  const [nomFiltre, setNomFiltre] = useEtatSession('calendrier.filtre.nom', '');
  // Periode (AAAA-MM-JJ, bornes incluses) ; vide = pas de borne.
  const [dateDebut, setDateDebut] = useEtatSession('calendrier.filtre.dateDebut', '');
  const [dateFin, setDateFin] = useEtatSession('calendrier.filtre.dateFin', '');
  const [villeFiltre, setVilleFiltre] = useEtatSession('calendrier.filtre.ville', '');
  const [regionsFiltre, setRegionsFiltre] = useEtatSession<string[]>('calendrier.filtre.regions', []);
  const [provincesFiltre, setProvincesFiltre] = useEtatSession<string[]>('calendrier.filtre.provinces', []);
  const [sourcesFiltre, setSourcesFiltre] = useEtatSession<string[]>('calendrier.filtre.sources', []);
  const [paysFiltre, setPaysFiltre] = useEtatSession('calendrier.filtre.pays', '');
  const [continentFiltre, setContinentFiltre] = useEtatSession('calendrier.filtre.continent', '');
  // Filtres, recherche et tri du tableau, conserves eux aussi pour la session.
  const [columnFilters, setColumnFilters] = useEtatSession<MRT_ColumnFiltersState>('calendrier.tableau.filtres', []);
  const [globalFilter, setGlobalFilter] = useEtatSession<string>('calendrier.tableau.recherche', '');
  const [sorting, setSorting] = useEtatSession<MRT_SortingState>('calendrier.tableau.tri', []);
  const basculerDistance = (champ: ChampDistance) =>
    setDistances((actuelles) =>
      actuelles.includes(champ) ? actuelles.filter((c) => c !== champ) : [...actuelles, champ],
    );
  // Fiche de saisie d'une course : creation, ou modification d'une course.
  const [fiche, setFiche] = useState<{ course?: Course } | null>(null);
  // Course a ajouter au calendrier personnel (fenetre de choix de la distance).
  const [courseAAjouter, setCourseAAjouter] = useState<Course | null>(null);
  // Seul le coach modifie un calendrier (l'athlete ne peut pas y ajouter de course).
  const moi = useMoi();
  const coach = estCoach(moi);
  // Courses importees (communes a tous) : imports et corrections reserves a l'administrateur.
  const admin = estAdmin(moi);
  const dateDuJour = useMemo(aujourdhui, []);
  const [chargementCourses, setChargementCourses] = useState(false);
  const [messageChargement, setMessageChargement] = useState<{ severity: 'success' | 'error'; texte: string } | null>(null);

  // Charge les courses depuis la source externe dans la base
  // (CalendrierControler / EventsService.save), puis recharge le tableau.
  // Menu des imports (charger / retirer une source), reserve a l'administrateur
  // (les courses importees sont communes a tous les utilisateurs).
  const [menuSources, setMenuSources] = useState<HTMLElement | null>(null);

  // Retire toutes les courses chargees par un import (courses perso gardees).
  const supprimerSource = async (source: 'ISKIO' | 'FFA') => {
    setMenuSources(null);
    if (!window.confirm(`Supprimer toutes les courses importées depuis ${SOURCES[source].libelle} ? Les courses que tu as ajoutées à la main sont conservées.`)) return;
    setChargementCourses(true);
    try {
      const response = await fetch('/api/events/source/' + source, {
        method: 'DELETE',
        headers: { 'Authorization': 'Bearer ' + sessionStorage.getItem('token') },
      });
      if (response.status !== 200) throw new Error('statut ' + response.status);
      const nombre = (await response.json()) as number;
      setMessageChargement({ severity: 'success', texte: `${nombre} course(s) ${SOURCES[source].libelle} supprimée(s).` });
      await queryClient.invalidateQueries({ queryKey: ['table-event'] });
    } catch (e) {
      setMessageChargement({ severity: 'error', texte: 'Suppression impossible (' + (e as Error).message + ')' });
    } finally {
      setChargementCourses(false);
    }
  };

  // source : "iskio" (Quebec / Canada) ou "athle" (calendrier FFA, France).
  const chargerCourses = async (source: 'iskio' | 'athle') => {
    setMenuSources(null);
    setChargementCourses(true);
    try {
      const response = await fetch('/api/events/save?source=' + source, {
        headers: { 'Authorization': 'Bearer ' + sessionStorage.getItem('token') },
      });
      if (response.status !== 200) {
        // Detail renvoye par le serveur (ex : reponse inattendue d'athle.fr).
        let detail = 'statut ' + response.status;
        try {
          const corps = await response.json();
          if (corps?.message) detail = corps.message;
        } catch {
          // corps non JSON
        }
        throw new Error(detail);
      }
      const nombre = (await response.json()) as number;
      setMessageChargement({
        severity: 'success',
        texte: nombre > 0 ? `${nombre} nouvelle(s) course(s) chargée(s).` : 'Aucune nouvelle course à charger.',
      });
      await queryClient.invalidateQueries({ queryKey: ['table-event'] });
    } catch (e) {
      setMessageChargement({ severity: 'error', texte: 'Chargement des courses impossible (' + (e as Error).message + ')' });
    } finally {
      setChargementCourses(false);
    }
  };

  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | undefined>
  >({});

  const columns = useMemo<MRT_ColumnDef<Course>[]>(
    () => [
      {
        accessorKey: 'id_calendrier',
        header: '#',
        enableEditing: false,
        size: 80        
      },
      {
        accessorKey: 'dateest',
        header: 'Date',
        enableEditing: true,
        size: 80        
      },
      {
        accessorKey: 'label',
        header: 'Course',
        Cell: ({ cell, row }) => (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            {row.original.lien
              ? <Link href={row.original.lien} target="_blank" rel="noopener">{cell.getValue<string>()}</Link>
              : cell.getValue<string>()}
            {row.original.personnelle && (
              <Tooltip title="Course ajoutée par toi : visible par toi seul">
                <Chip label="Perso" size="small" color="secondary" variant="outlined" />
              </Tooltip>
            )}
          </Box>
        ),
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.label,
          helperText: validationErrors?.label,
          //remove any previous validation errors when Course focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              label: undefined,
            }),
          //optionally add validation checking for onBlur or onChange
        },
      },
      {
        accessorKey: 'source',
        header: 'Source',
        size: 90,
        enableEditing: false,
        filterVariant: 'select',
        filterSelectOptions: Object.entries(SOURCES).map(([value, { libelle }]) => ({ value, label: libelle })),
        Cell: ({ cell }) => {
          const source = SOURCES[cell.getValue<string>() ?? ''];
          return source ? (
            <Tooltip title={source.description}>
              <Chip size="small" variant="outlined" label={source.libelle} />
            </Tooltip>
          ) : null;
        },
      },
      {
        accessorKey: 'continent',
        header: 'Continent',
        size: 130,
        enableEditing: false,
      },
      {
        id: 'pays',
        accessorFn: (course) => paysDe(course),
        header: 'Pays',
        size: 120,
        enableEditing: false,
      },
      {
        accessorKey: 'province',
        header: 'Province',
        editVariant: 'select',
        editSelectOptions: usStates,
        muiEditTextFieldProps: {
          select: true,
          error: !!validationErrors?.province,
          helperText: validationErrors?.province,
        },
      },
      {
        accessorKey: 'region',
        header: 'Région',
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.region,
          helperText: validationErrors?.region,
          //remove any previous validation errors when Course focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              region: undefined,
            }),
        },
      },
      {
        accessorKey: 'city',
        header: 'Ville',
        muiEditTextFieldProps: {
          type: 'city',
          required: true,
          error: !!validationErrors?.city,
          helperText: validationErrors?.city,
          //remove any previous validation errors when Course focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              city: undefined,
            }),
        },
      },
      {
        // Nombre de participants fourni par la source (nbfin) ; tri numerique,
        // courses sans information en fin de liste.
        id: 'nb_participants',
        accessorFn: (course) => course.nb_participants ?? undefined,
        header: 'Participants',
        size: 110,
        enableEditing: false,
        sortUndefined: 'last',
        filterVariant: 'range',
        Cell: ({ cell }) => {
          const valeur = cell.getValue<number | undefined>();
          return valeur === undefined ? '—' : valeur.toLocaleString('fr-FR');
        },
        muiTableHeadCellProps: { align: 'right' },
        muiTableBodyCellProps: { align: 'right' },
      },
      {
        accessorKey: 'indicateur_boston',
        id: 'indicateur_boston',
        size: 80,
        header: 'Boston',
        Cell: ({ cell }) => (
          BoxIndicateurCourse(cell.getValue<string>(), "true", "false", "BQ", "X")
        ),        
          muiEditTextFieldProps: {
          required: false,
          error: !!validationErrors?.indicateur_boston,
          helperText: validationErrors?.indicateur_boston,
          //remove any previous validation errors when Course focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              indicateur_boston: undefined,
            }),
        },
      },
      {
        accessorKey: 'indicateur_5k',
        header: '5k',
        size: 80,
        Cell: ({ cell, column, renderedCellValue, row }) => (
          BoxIndicateurCourse(cell.getValue<string>(), "true", "false", "5k", "X")          
        )
      },     
      {
        accessorKey: 'indicateur_10k',
        header: '10k',
        size: 80,
        Cell: ({ cell }) => (
          BoxIndicateurCourse(cell.getValue<string>(), "true", "false", "10k", "X")
          ),        
      },
      {
        accessorKey: 'indicateur_21k',
        header: '21k',
        size: 80,
        Cell: ({ cell }) => (
          BoxIndicateurCourse(cell.getValue<string>(), "true", "false", "21k", "X")
          ),        

      },
      {
        accessorKey: 'indicateur_42k',
        header: '42k',
        size: 80,
        Cell: ({ cell }) => (
          BoxIndicateurCourse(cell.getValue<string>(), "true", "false", "42k", "X")
          ),        

      }
    ],
    [validationErrors],
  );

  //call CREATE hook
  const { mutateAsync: createCourse, isPending: isCreatingCourse } =
    useCreateCourse();
  
    //call READ hook
  const {
    data: { data = [], meta } = {}, //your data and api response will probably be different
//    data: fetchedCourses = [],
    isError: isLoadingCoursesError,
    isFetching: isFetchingCourses,
    isLoading: isLoadingCourses,
  } = useGetCourses();
  //call UPDATE hook
  const { mutateAsync: updateCourse, isPending: isUpdatingCourse } =
    useUpdateCourse();
  //call DELETE hook
  const { mutateAsync: deleteCourse, isPending: isDeletingCourse } =
    useDeleteCourse();

  //CREATE action
  const handleCreateCourse: MRT_TableOptions<Course>['onCreatingRowSave'] = async ({
    values,
    table,
  }) => {
    const newValidationErrors = validateCourse(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }
    setValidationErrors({});
    await createCourse(values);
    table.setCreatingRow(null); //exit creating mode
  };

  //UPDATE action
  const handleSaveCourse: MRT_TableOptions<Course>['onEditingRowSave'] = async ({
    values,
    table,
  }) => {
    const newValidationErrors = validateCourse(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }
    setValidationErrors({});
    await updateCourse(values);
    table.setEditingRow(null); //exit editing mode
  };

  //DELETE action
  const openDeleteConfirmModal = (row: MRT_Row<Course>) => {
    if (window.confirm('Voulez-vous vraiment supprimer cette course ?')) {
      deleteCourse(row.original.id_calendrier).catch((e: Error) =>
        setMessageChargement({ severity: 'error', texte: 'Suppression impossible : ' + e.message }),
      );
    }
  };

  // Valeurs deja utilisees, proposees en suggestion dans la fiche.
  const valeursDistinctes = (champ: 'region' | 'province') =>
    Array.from(new Set(data.map((c: Course) => (c[champ] ?? '').trim()).filter((v: string) => v !== ''))).sort() as string[];
  const regions = useMemo(() => valeursDistinctes('region'), [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const provinces = useMemo(() => valeursDistinctes('province'), [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pays presents, et regions proposees (celles du pays choisi, le cas echeant).
  const continentDe = (c: Course) => (c.continent ?? '').trim() || 'Non renseigné';
  const continentsDisponibles = useMemo(
    () => Array.from(new Set(data.map((c: Course) => continentDe(c)))).sort((a, b) => (a as string).localeCompare(b as string, 'fr')) as string[],
    [data],
  );
  const paysDisponibles = useMemo(
    () =>
      Array.from(new Set(
        data.filter((c: Course) => !continentFiltre || continentDe(c) === continentFiltre).map((c: Course) => paysDe(c)),
      )).sort((a, b) => (a as string).localeCompare(b as string, 'fr')) as string[],
    [data, continentFiltre],
  );
  // Provinces proposees : celles du continent / pays choisis.
  const provincesDisponibles = useMemo(
    () =>
      Array.from(new Set(
        data
          .filter((c: Course) => (!paysFiltre || paysDe(c) === paysFiltre) && (!continentFiltre || continentDe(c) === continentFiltre))
          .map((c: Course) => (c.province ?? '').trim())
          .filter((p: string) => p !== ''),
      )).sort((a, b) => (a as string).localeCompare(b as string, 'fr')) as string[],
    [data, paysFiltre, continentFiltre],
  );
  // Regions proposees : celles du continent / pays / provinces choisis.
  const regionsDisponibles = useMemo(
    () =>
      Array.from(new Set(
        data
          .filter((c: Course) => (!paysFiltre || paysDe(c) === paysFiltre) && (!continentFiltre || continentDe(c) === continentFiltre)
            && (provincesFiltre.length === 0 || provincesFiltre.includes((c.province ?? '').trim())))
          .map((c: Course) => (c.region ?? '').trim())
          .filter((r: string) => r !== ''),
      )).sort((a, b) => (a as string).localeCompare(b as string, 'fr')) as string[],
    [data, paysFiltre, continentFiltre, provincesFiltre],
  );

  const donneesAffichees = useMemo(() => {
    const nom = normaliser(nomFiltre.trim());
    const ville = normaliser(villeFiltre.trim());
    const periode = !!dateDebut || !!dateFin;
    return data.filter((course: Course) => {
      const date = course.dateest ? String(course.dateest).substring(0, 10) : '';
      return (
      // Avec une periode, elle seule decide (courses passees comprises).
      (periode
        ? !!date && (!dateDebut || date >= dateDebut) && (!dateFin || date <= dateFin)
        : afficherPassees || !estPassee(course, dateDuJour))
      && (distances.length === 0 || distances.some((champ) => estVrai(course[champ])))
      && (!seulementBoston || estVrai(course.indicateur_boston))
      && (!nom || normaliser(course.label ?? '').includes(nom))
      && (!ville || normaliser(course.city ?? '').includes(ville))
      && (regionsFiltre.length === 0 || regionsFiltre.includes((course.region ?? '').trim()))
      && (provincesFiltre.length === 0 || provincesFiltre.includes((course.province ?? '').trim()))
      && (!paysFiltre || paysDe(course) === paysFiltre)
      && (!continentFiltre || continentDe(course) === continentFiltre)
      && (sourcesFiltre.length === 0 || sourcesFiltre.includes(course.source ?? ''))
      );
    });
  }, [data, afficherPassees, dateDuJour, dateDebut, dateFin, distances, seulementBoston, nomFiltre, villeFiltre, regionsFiltre, provincesFiltre, paysFiltre, continentFiltre, sourcesFiltre]);
  const filtresLieu = !!dateDebut || !!dateFin || !!nomFiltre.trim() || sourcesFiltre.length > 0 || !!villeFiltre.trim() || !!continentFiltre || regionsFiltre.length > 0
    || provincesFiltre.length > 0 || !!paysFiltre;

  const table = useMaterialReactTable({
    columns,
    data: donneesAffichees,
    // Courses passees (visibles seulement si demande) : grisees.
    muiTableBodyRowProps: ({ row }) =>
      estPassee(row.original, dateDuJour)
        ? {
            sx: {
              backgroundColor: 'action.hover',
              '& td': { color: 'text.disabled' },
              '& td a': { color: 'text.disabled' },
              '& td img, & td svg': { opacity: 0.5 },
            },
          }
        : {},
    initialState: { showColumnFilters: false, columnVisibility: { id_calendrier: false }, density: 'compact', pagination: { pageIndex: 0, pageSize: 15 } }, //hide firstName column by default

    //manualFiltering: true, //turn off built-in client-side filtering
    //manualPagination: true, //turn off built-in client-side pagination
    //manualSorting: true, //turn off built-in client-side sorting
    createDisplayMode: 'modal', //default ('row', and 'custom' are also available)
    editDisplayMode: 'modal', //default ('row', 'cell', 'table', and 'custom' are also available)
    enableEditing: true,
    getRowId: (row) => row.id_calendrier,
    muiToolbarAlertBannerProps: isLoadingCoursesError
      ? {
          color: 'error',
          children: 'Erreur lors du chargement des courses',
        }
      : undefined,
    muiTableContainerProps: {
      sx: {
        minHeight: '500px',
      },
    },
    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateCourse,
    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: handleSaveCourse,
    //optionally customize modal content
    renderCreateRowDialogContent: ({ table, row, internalEditComponents }) => (
      <React.Fragment>
        <DialogTitle variant="h3">Nouvelle course</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
        >
          {internalEditComponents}         
        </DialogContent>
        <DialogActions>
          <MRT_EditActionButtons variant="text" table={table} row={row} />
        </DialogActions>
      </React.Fragment>
    ),
    //optionally customize modal content
    renderEditRowDialogContent: ({ table, row, internalEditComponents }) => (
      <>
        <DialogTitle variant="h3">Modifier la course</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
        >
          {internalEditComponents} {/* or render custom edit components here */}
        </DialogContent>
        <DialogActions>
          <MRT_EditActionButtons variant="text" table={table} row={row} />
        </DialogActions>
      </>
    ),
    // Les courses ajoutees par l'utilisateur sont modifiables et supprimables ;
    // les courses importees ne sont corrigees que par l'administrateur (cadenas sinon).
    // Colonne d'actions assez large pour "ajouter au calendrier" + modifier/supprimer.
    displayColumnDefOptions: { 'mrt-row-actions': { size: 160 } },
    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {coach && (
      <Tooltip title="Ajouter au calendrier (choix de la distance)">
        <IconButton color="primary" onClick={() => setCourseAAjouter(row.original)}>
          <EventAvailableIcon />
        </IconButton>
      </Tooltip>
      )}
      {(row.original.personnelle || admin) && (
      <Tooltip title={row.original.personnelle ? 'Modifier' : 'Modifier cette course importée (corrections visibles par tous)'}>
        <IconButton onClick={() => setFiche({ course: row.original })}>
          <EditIcon />
        </IconButton>
      </Tooltip>
      )}
      {row.original.personnelle ? (
        <Tooltip title="Supprimer">
          <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title={admin
          ? 'Course importée : elle ne peut pas être supprimée (voir le menu Imports pour retirer toute une source)'
          : 'Course importée : commune à tous, elle ne peut être ni modifiée ni supprimée'}>
          <LockOutlinedIcon fontSize="small" color="disabled" sx={{ ml: 1 }} />
        </Tooltip>
      )}
      </Box>
    ),
    renderTopToolbarCustomActions: ({ table }) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
      <Button
        variant="contained"
        onClick={() => {
          setFiche({}); // fiche de creation (cf FicheCourse)
          //or you can pass in a row object to set default values with the `createRow` helper function
          // table.setCreatingRow(
          //   createRow(table, {
          //     //optionally pass in default values for the new row, useful for nested data or other complex scenarios
          //   }),
          // );
        }}
      >
        Ajouter une course
      </Button>
      {admin && (
        <>
          <Button
            variant="outlined"
            startIcon={<CloudDownloadIcon />}
            endIcon={<ArrowDropDownIcon />}
            onClick={(e) => setMenuSources(e.currentTarget)}
            disabled={chargementCourses}
          >
            {chargementCourses ? 'Traitement…' : 'Imports'}
          </Button>
          <Menu anchorEl={menuSources} open={!!menuSources} onClose={() => setMenuSources(null)}>
            <MenuItem onClick={() => chargerCourses('iskio')}>
              <ListItemIcon><CloudDownloadIcon fontSize="small" /></ListItemIcon>
              Charger les courses du Québec (iskio)
            </MenuItem>
            <MenuItem onClick={() => chargerCourses('athle')}>
              <ListItemIcon><CloudDownloadIcon fontSize="small" /></ListItemIcon>
              Charger les courses de France (FFA) — quelques minutes
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => supprimerSource('ISKIO')}>
              <ListItemIcon><DeleteSweepIcon fontSize="small" color="error" /></ListItemIcon>
              Supprimer les courses iskio
            </MenuItem>
            <MenuItem onClick={() => supprimerSource('FFA')}>
              <ListItemIcon><DeleteSweepIcon fontSize="small" color="error" /></ListItemIcon>
              Supprimer les courses FFA
            </MenuItem>
          </Menu>
        </>
      )}
      </Box>
    ),
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    state: {
      columnFilters,
      globalFilter,
      sorting,
      isLoading: isLoadingCourses,
      isSaving: isCreatingCourse || isUpdatingCourse || isDeletingCourse,
      showAlertBanner: isLoadingCoursesError,
      showProgressBars: isFetchingCourses,
    }
  });

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.875rem', mr: 0.5 }}>
          Distances :
        </Box>
        {FILTRES_DISTANCE.map(({ champ, libelle }) => {
          const actif = distances.includes(champ);
          return (
            <Chip
              key={champ}
              label={libelle}
              color={actif ? 'primary' : 'default'}
              variant={actif ? 'filled' : 'outlined'}
              onClick={() => basculerDistance(champ)}
              aria-pressed={actif}
            />
          );
        })}
        <Chip
          label="Qualificative Boston"
          color={seulementBoston ? 'success' : 'default'}
          variant={seulementBoston ? 'filled' : 'outlined'}
          onClick={() => setSeulementBoston((v) => !v)}
          aria-pressed={seulementBoston}
          sx={{ ml: 1 }}
        />
        {(distances.length > 0 || seulementBoston) && (
          <Button
            size="small"
            onClick={() => {
              setDistances([]);
              setSeulementBoston(false);
            }}
          >
            Tout afficher
          </Button>
        )}
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        <FormControlLabel
          sx={{ ml: 0 }}
          control={
            <Switch
              checked={afficherPassees || !!dateDebut || !!dateFin}
              disabled={!!dateDebut || !!dateFin}
              onChange={(e) => setAfficherPassees(e.target.checked)}
            />
          }
          label="Afficher les courses passées"
        />
      </Box>
      {/* Filtres, dans l'ordre des colonnes : date, course, source, continent, pays, province, region, ville */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
        <TextField
          size="small"
          type="date"
          label="Du"
          value={dateDebut}
          onChange={(e) => setDateDebut(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: dateFin || undefined }}
          sx={{ width: 160 }}
        />
        <TextField
          size="small"
          type="date"
          label="Au"
          value={dateFin}
          onChange={(e) => setDateFin(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: dateDebut || undefined }}
          sx={{ width: 160 }}
        />
        <TextField
          size="small"
          label="Course contient"
          value={nomFiltre}
          onChange={(e) => setNomFiltre(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 220 }}
        />
        <TextField
          select
          size="small"
          label="Sources"
          value={sourcesFiltre}
          onChange={(e) => {
            const v = e.target.value as unknown as string | string[];
            setSourcesFiltre(typeof v === 'string' ? v.split(',').filter(Boolean) : v);
          }}
          SelectProps={{
            multiple: true,
            displayEmpty: true,
            renderValue: (selection) => {
              const choix = selection as string[];
              return choix.length === 0 ? 'Toutes' : choix.map((c) => SOURCES[c]?.libelle ?? c).join(', ');
            },
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        >
          {Object.entries(SOURCES).map(([code, { libelle, description }]) => (
            <MenuItem key={code} value={code} dense>
              <Checkbox size="small" checked={sourcesFiltre.includes(code)} sx={{ p: 0.5, mr: 1 }} />
              <ListItemText primary={libelle} secondary={description} />
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Continent"
          value={continentFiltre}
          onChange={(e) => {
            setContinentFiltre(e.target.value);
            setPaysFiltre('');
            setProvincesFiltre([]);
            setRegionsFiltre([]);
          }}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">Tous</MenuItem>
          {continentsDisponibles.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
        <TextField
          select
          size="small"
          label="Pays"
          value={paysFiltre}
          onChange={(e) => {
            setPaysFiltre(e.target.value);
            setProvincesFiltre([]);
            setRegionsFiltre([]);
          }}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">Tous</MenuItem>
          {paysDisponibles.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
        </TextField>
        <TextField
          select
          size="small"
          label="Provinces"
          value={provincesFiltre}
          onChange={(e) => {
            const v = e.target.value as unknown as string | string[];
            setProvincesFiltre(typeof v === 'string' ? v.split(',').filter(Boolean) : v);
            setRegionsFiltre([]);
          }}
          SelectProps={{
            multiple: true,
            displayEmpty: true,
            renderValue: (selection) => {
              const choix = selection as string[];
              return choix.length === 0 ? 'Toutes' : choix.join(', ');
            },
            MenuProps: { PaperProps: { sx: { maxHeight: 400 } } },
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150, maxWidth: 260 }}
        >
          {provincesDisponibles.map((p) => (
            <MenuItem key={p} value={p} dense>
              <Checkbox size="small" checked={provincesFiltre.includes(p)} sx={{ p: 0.5, mr: 1 }} />
              <ListItemText primary={p} />
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Régions"
          value={regionsFiltre}
          onChange={(e) => {
            const v = e.target.value as unknown as string | string[];
            setRegionsFiltre(typeof v === 'string' ? v.split(',').filter(Boolean) : v);
          }}
          SelectProps={{
            multiple: true,
            displayEmpty: true,
            renderValue: (selection) => {
              const choix = selection as string[];
              return choix.length === 0 ? 'Toutes' : choix.join(', ');
            },
            MenuProps: { PaperProps: { sx: { maxHeight: 400 } } },
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 200, maxWidth: 340 }}
        >
          {regionsDisponibles.map((r) => (
            <MenuItem key={r} value={r} dense>
              <Checkbox size="small" checked={regionsFiltre.includes(r)} sx={{ p: 0.5, mr: 1 }} />
              <ListItemText primary={r} />
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Ville contient"
          value={villeFiltre}
          onChange={(e) => setVilleFiltre(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 190 }}
        />
        {filtresLieu && (
          <>
            <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              {donneesAffichees.length} course(s)
            </Box>
            <Button
              size="small"
              onClick={() => {
                setDateDebut('');
                setDateFin('');
                setSourcesFiltre([]);
                setNomFiltre('');
                setVilleFiltre('');
                setContinentFiltre('');
                setPaysFiltre('');
                setProvincesFiltre([]);
                setRegionsFiltre([]);
              }}
            >
              Effacer
            </Button>
          </>
        )}
      </Box>
      <MaterialReactTable table={table} />
      <AjoutCourseCalendrier course={courseAAjouter} onFermer={() => setCourseAAjouter(null)} />
      <FicheCourse
        ouverte={fiche !== null}
        course={fiche?.course}
        regions={regions}
        provinces={provinces}
        onFermer={() => setFiche(null)}
        onEnregistrer={(course: Course) => (fiche?.course ? updateCourse(course) : createCourse(course))}
      />
      {messageChargement && (
        <Snackbar open autoHideDuration={6000} onClose={() => setMessageChargement(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity={messageChargement.severity} onClose={() => setMessageChargement(null)}>
            {messageChargement.texte}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};


const queryClient = new QueryClient();

const ExampleWithProviders = () => (
  //Put this with your other react-query providers near root of your app
  <Box component="main" sx={{ p: 12, pt: 10 }}>
    <QueryClientProvider client={queryClient}>
      <Example />
    </QueryClientProvider>
  </Box>
);

export default ExampleWithProviders;

const validateRequired = (value: string) => !!value.length;
const validatecity = (city: string) => validateRequired(city);

function validateCourse(Course: Course) {
  return {
    label: !validateRequired(Course.label)
      ? 'Le nom de la course est obligatoire'
      : '',
    region: !validateRequired(Course.region) ? 'La région est obligatoire' : '',
    city: !validatecity(Course.city) ? 'La ville est obligatoire' : '',
  };
}